const API_GATEWAY_ID = "ptbh4jt7ye";

// Local backend endpoint (for development)
const LOCAL_API_ENDPOINT = import.meta.env.VITE_API_ROOT_ENDPOINT;

// Full AWS Gateway endpoint URL (for production)
const API_GATEWAY_ENDPOINT = `https://${API_GATEWAY_ID}.execute-api.ca-central-1.amazonaws.com/beta/api`;

// Create a manager that smartly picks between localhost and AWS
class ApiEndpointManager {
    private _endpoint: string; // Stores the current chosen endpoint (local or AWS)
    private _initialized: boolean = false; // Whether we've already checked and finalized the endpoint
    private _initializationPromise: Promise<void> | null = null; // Promise to track if we're still checking

    constructor() {
        // Start with either an env variable or default to AWS Gateway
        this._endpoint = LOCAL_API_ENDPOINT || API_GATEWAY_ENDPOINT;

        // If we're NOT using localhost, we can say we're already initialized (no need to check localhost)
        this._initialized = !this._endpoint.includes("localhost");
    }

    // Initialize the manager (only once)
    private initialize(): Promise<void> {
        // If already initialized, nothing to do (i.e., we've already checked whether localhost is available)
        if (this._initialized) {
            return Promise.resolve();
        }

        // If not yet initialized, start checking localhost
        if (!this._initializationPromise) {
            // Store the promise so we don't start multiple checks at once
            this._initializationPromise = this.checkLocalhost().then((isAvailable) => {
                // If localhost is not available, switch to API Gateway
                if (!isAvailable && this._endpoint.includes("localhost")) {
                    // console.log("Localhost API not accessible, switching to API Gateway");
                    this._endpoint = API_GATEWAY_ENDPOINT;
                }

                // After checking, we're now initialized
                this._initialized = true;
                console.log(`Using API endpoint: ${this._endpoint}`);
            });
        }

        return this._initializationPromise;
    }

    // Actually test if localhost is up and running
    private async checkLocalhost(): Promise<boolean> {
        // If we're not trying localhost, don't bother checking
        if (!this._endpoint.includes("localhost")) {
            return false;
        }

        try {
            // For CORS issues, we'll use a different approach that works even with CORS errors
            // We'll consider localhost available if we get any response (even a CORS error)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1000); // 1000ms timeout

            // We'll use an image fetching strategy which is less likely to have CORS issues
            // or check for a network error rather than examining the response
            const url = new URL(LOCAL_API_ENDPOINT);
            const testUrl = `${url.protocol}//${url.hostname}:${url.port}/`;

            try {
                const response = await fetch(testUrl, {
                    method: "HEAD",
                    signal: controller.signal,
                    mode: "no-cors", // This allows us to detect if the server is running even if it returns CORS errors
                });

                clearTimeout(timeoutId);

                // If we get here, the server is running (even if it returns CORS errors)
                // console.log("Localhost is available, using local endpoint");
                return true;
            } catch (error: unknown) {
                // If the error is an AbortError (timeout), the server is not running
                if (error instanceof Error && error.name === "AbortError") {
                    // console.log("Localhost check timed out");
                    return false;
                }

                // If we get a TypeError with "Failed to fetch", the server is not running
                if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
                    // console.log("Localhost server is not running");
                    return false;
                }

                // For any other errors, assume the server is running but has CORS issues
                console.log(
                    "Received error from localhost but server appears to be running:",
                    error instanceof Error ? error.message : error,
                );
                return true;
            } finally {
                clearTimeout(timeoutId);
            }
        } catch (error) {
            // If any other error happens, assume localhost is not available
            console.error("Error checking localhost:", error);
            return false;
        }
    }

    // Public method to get the right endpoint (waits for initialization if needed)
    async getEndpoint(): Promise<string> {
        await this.initialize(); // Make sure initialization is finished
        return this._endpoint;
    }

    get endpoint(): string {
        if (!this._initialized) {
            console.warn(
                "Warning: Accessed API endpoint before it was properly initialized!",
                this._endpoint,
            );
        }

        return this._endpoint;
    }

    // Force switching to API Gateway manually if needed
    useApiGateway(): void {
        this._endpoint = API_GATEWAY_ENDPOINT;
        this._initialized = true;
        console.log(`Forced switch to API Gateway: ${this._endpoint}`);
    }
}

// Create one and only one instance of the manager
const apiEndpointManager = new ApiEndpointManager();
apiEndpointManager.getEndpoint().catch(console.error);
export default apiEndpointManager;
